import { useMemo } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';
import { keycloakApi } from '@/api/keycloak';
import type { KeycloakUser, ListUsersResponse } from '@/api/keycloak';
import mockData from '@/data/mock-data.json';

interface AccessUser {
  id: string;
  objectId: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  mfaEnabled: boolean;
  loginMethod: string;
  userType: string;
  status: string;
  roles: string[];
  groups: string[];
  createdAt: string;
  lastLogin: string | undefined;
}

interface UseKeycloakUsersParams {
  search?: string;
  first?: number;
  max?: number;
}

interface UseKeycloakUsersResult {
  users: AccessUser[];
  total: number;
  isLoading: boolean;
  error: string | undefined;
  isUsingMockData: boolean;
  reload: () => void;
}

const mapKeycloakUserToAccessUser = (user: KeycloakUser): AccessUser => ({
  id: user.id,
  objectId: user.id,
  firstName: user.firstName ?? '',
  lastName: user.lastName ?? '',
  email: user.email ?? '',
  username: user.username,
  mfaEnabled: user.attributes?.mfaEnabled?.[0] === 'true',
  loginMethod: user.attributes?.loginMethod?.[0] ?? 'Azure AD',
  userType: user.attributes?.userType?.[0] ?? 'internal',
  status: user.enabled ? 'active' : 'suspended',
  roles: user.attributes?.roles ?? [],
  groups: user.groups?.map((g) => g.groupId) ?? [],
  createdAt: user.createdTimestamp
    ? new Date(user.createdTimestamp).toISOString()
    : new Date().toISOString(),
  lastLogin: user.attributes?.lastLogin?.[0],
});

const mockFallback: ListUsersResponse = {
  users: [],
  total: (mockData.accessManagementUsers as unknown as AccessUser[]).length,
};

export const useKeycloakUsers = (
  params: UseKeycloakUsersParams = {},
): UseKeycloakUsersResult => {
  const { search, first, max } = params;

  const { data, isLoading, error, isUsingMockData, reload } =
    useApiQuery<ListUsersResponse>(
      {
        queryFn: (signal) => {
          void signal;
          return keycloakApi.listUsers({ search, first, max });
        },
        mockData: mockFallback,
      },
      [search, first, max],
    );

  const users = useMemo(() => {
    if (isUsingMockData) {
      return mockData.accessManagementUsers.map((u) => ({
        ...u,
        lastLogin: u.lastLogin ?? undefined,
      })) as AccessUser[];
    }
    return data?.users.map(mapKeycloakUserToAccessUser) ?? [];
  }, [data, isUsingMockData]);

  const total = useMemo(() => {
    if (isUsingMockData) {
      return (mockData.accessManagementUsers as unknown as AccessUser[]).length;
    }
    return data?.total ?? 0;
  }, [data, isUsingMockData]);

  return { users, total, isLoading, error, isUsingMockData, reload };
};
